import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { staffApi, ApiError } from "@/api";
import type { Quote, VatTreatment } from "@/types";
import { isTerminal } from "@/lib/status";
import { computeTotals, formatMoney, defaultSelectedOptionalIds } from "@/lib/money";
import { Button, StatusPill, FullPageSpinner, TextField, SelectField } from "@/components/ui";
import { LineItemsEditor } from "./LineItemsEditor";
import { QuoteEngagement } from "./QuoteEngagement";
import { AcceptOnBehalfModal } from "./AcceptOnBehalfModal";
import "./quote-editor.css";

const VAT_OPTIONS: { value: VatTreatment; label: string }[] = [
  { value: "exclusive", label: "Exclusive (add VAT)" },
  { value: "inclusive", label: "Inclusive (VAT in prices)" },
  { value: "zero_rated", label: "Zero-rated" },
  { value: "not_applicable", label: "Not applicable" },
];

/** Quotes are VAT-inclusive for now; the picker is hidden but kept wired up so
 *  it can be re-enabled by flipping this flag if VAT handling needs to vary. */
const SHOW_VAT_TREATMENT = false;

export function QuoteEditor() {
  const { quoteId = "" } = useParams();
  const id = Number(quoteId);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [savingField, setSavingField] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState<"publish" | "withdraw" | "reopen" | null>(null);
  const [showAcceptOnBehalf, setShowAcceptOnBehalf] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      setQuote(await staffApi.getQuote(id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load this quote.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function saveField(field: keyof Quote, value: unknown) {
    if (!quote) return;
    setSavingField(field);
    try {
      const updated = await staffApi.updateQuote(quote.id, { [field]: value } as Partial<Quote>);
      // Merge — keep line_items the PATCH response may omit.
      setQuote((prev) => (prev ? { ...prev, ...updated } : updated));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save.");
    } finally {
      setSavingField(null);
    }
  }

  async function publish() {
    if (!quote) return;
    setActionBusy("publish");
    setError(undefined);
    try {
      const updated = await staffApi.publishQuote(quote.id);
      setQuote((prev) => (prev ? { ...prev, ...updated } : updated));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not publish.");
    } finally {
      setActionBusy(null);
    }
  }

  async function withdraw() {
    if (!quote) return;
    setActionBusy("withdraw");
    setError(undefined);
    try {
      const updated = await staffApi.withdrawQuote(quote.id);
      setQuote((prev) => (prev ? { ...prev, ...updated } : updated));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not withdraw.");
    } finally {
      setActionBusy(null);
    }
  }

  async function reopen() {
    if (!quote) return;
    setActionBusy("reopen");
    setError(undefined);
    try {
      await staffApi.reopenQuote(quote.id);
      // Re-fetch rather than merge: reopening also clears decline stamps and
      // re-arms the expiry, and the envelope carries lead ids, not quote fields.
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reopen.");
    } finally {
      setActionBusy(null);
    }
  }

  if (loading) return <FullPageSpinner label="Loading quote…" />;
  if (error && !quote) {
    return (
      <div className="quote-editor__error-page">
        <p className="quotes-list__error" role="alert">{error}</p>
        <Link to="/staff/quotes"><Button variant="secondary">Back to quotes</Button></Link>
      </div>
    );
  }
  if (!quote) return null;

  // Seed the default-on optionals so Total mirrors the quote's headline value
  // (server quote_value, the quotes list, the WhatConverts push) and the public
  // page's initial state — not just the mandatory base.
  const lineItems = quote.line_items ?? [];
  const totals = computeTotals(
    lineItems,
    quote.vat_treatment || "inclusive",
    defaultSelectedOptionalIds(lineItems),
  );
  const terminal = isTerminal(quote.status);
  const publicUrl =
    quote.public_slug ? `${window.location.origin}/${quote.public_slug}` : null;

  return (
    <section className="quote-editor">
      <header className="quote-editor__head">
        <div>
          <Link to="/staff/quotes" className="quote-editor__back">← All quotes</Link>
          <h1 className="numeric">{quote.quote_number}</h1>
          <div className="quote-editor__status"><StatusPill status={quote.status} /></div>
        </div>
        <div className="quote-editor__head-actions">
          <Link to={`/staff/quotes/${quote.id}/edit`} className="quote-editor__edit-link">
            <Button variant="secondary">Edit content</Button>
          </Link>
          {quote.status === "draft" ? (
            <Button variant="primary" onClick={publish} loading={actionBusy === "publish"}>
              Publish
            </Button>
          ) : null}
          {(quote.status === "sent" || quote.status === "viewed") ? (
            <>
              <Button variant="primary" onClick={publish} loading={actionBusy === "publish"}>
                Re-publish
              </Button>
              <Button variant="secondary" onClick={() => setShowAcceptOnBehalf(true)}>
                Accept on behalf
              </Button>
              <Button variant="danger" onClick={withdraw} loading={actionBusy === "withdraw"}>
                Withdraw
              </Button>
            </>
          ) : null}
          {(quote.status === "expired" || quote.status === "declined") ? (
            <Button variant="primary" onClick={reopen} loading={actionBusy === "reopen"}>
              Reopen
            </Button>
          ) : null}
        </div>
      </header>

      {error ? <p className="quotes-list__error" role="alert">{error}</p> : null}

      {publicUrl ? (
        <div className="quote-editor__public-link">
          <span className="eyebrow">Public link</span>
          <a href={publicUrl} target="_blank" rel="noreferrer">{publicUrl}</a>
        </div>
      ) : (
        <p className="quote-editor__no-link">Not published yet — no public link until you publish.</p>
      )}

      <div className="quote-editor__grid">
        <div className="quote-editor__col">
          <h2>Contact</h2>
          <div className="quote-editor__two">
            <FieldRow
              label="First name"
              value={quote.contact_first_name}
              saving={savingField === "contact_first_name"}
              onSave={(v) => saveField("contact_first_name", v)}
            />
            <FieldRow
              label="Last name"
              value={quote.contact_last_name}
              saving={savingField === "contact_last_name"}
              onSave={(v) => saveField("contact_last_name", v)}
            />
          </div>
          <FieldRow
            label="Company name"
            value={quote.company_name}
            saving={savingField === "company_name"}
            onSave={(v) => saveField("company_name", v)}
          />
          <div className="quote-editor__two">
            <FieldRow
              label="Email"
              type="email"
              value={quote.email}
              saving={savingField === "email"}
              onSave={(v) => saveField("email", v)}
            />
            <FieldRow
              label="Phone"
              value={quote.phone}
              saving={savingField === "phone"}
              onSave={(v) => saveField("phone", v)}
            />
          </div>

          <h2>Project</h2>
          <FieldRow
            label="Short description"
            value={quote.project_description}
            saving={savingField === "project_description"}
            onSave={(v) => saveField("project_description", v)}
          />
          <div className="quote-editor__two">
            <FieldRow
              label="Site address line 1"
              value={quote.site_address_line_1}
              saving={savingField === "site_address_line_1"}
              onSave={(v) => saveField("site_address_line_1", v)}
            />
            <FieldRow
              label="Postcode"
              value={quote.site_postcode}
              saving={savingField === "site_postcode"}
              onSave={(v) => saveField("site_postcode", v)}
            />
          </div>
          {SHOW_VAT_TREATMENT ? (
            <SelectField
              label="VAT treatment"
              value={quote.vat_treatment || "inclusive"}
              disabled={savingField === "vat_treatment"}
              onChange={(e) => saveField("vat_treatment", e.target.value as VatTreatment)}
            >
              {VAT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </SelectField>
          ) : null}
        </div>

        <div className="quote-editor__col">
          <h2>Line items</h2>
          <p className="quote-editor__hint">
            Line items are the single source of truth for pricing. The public pricing block
            renders from these rows.
          </p>
          <LineItemsEditor
            quoteId={quote.id}
            items={quote.line_items ?? []}
            currency={quote.currency}
            disabled={terminal}
            onChanged={reload}
          />

          <dl className="quote-editor__totals">
            <div><dt>Subtotal</dt><dd className="numeric">{formatMoney(totals.subtotal, quote.currency)}</dd></div>
            <div><dt>{totals.vatLabel}</dt><dd className="numeric">{formatMoney(totals.vat, quote.currency)}</dd></div>
            <div className="quote-editor__totals-total"><dt>Total</dt><dd className="numeric">{formatMoney(totals.total, quote.currency)}</dd></div>
            {totals.selectedOptionalTotal > 0 ? (
              <div className="quote-editor__totals-note">
                <dt>Default-on options (in total)</dt>
                <dd className="numeric">{formatMoney(totals.selectedOptionalTotal, quote.currency)}</dd>
              </div>
            ) : null}
            {totals.optionalTotal > totals.selectedOptionalTotal ? (
              <div className="quote-editor__totals-note">
                <dt>Optional extras</dt>
                <dd className="numeric">
                  + {formatMoney(totals.optionalTotal - totals.selectedOptionalTotal, quote.currency)}
                </dd>
              </div>
            ) : null}
          </dl>
        </div>
      </div>

      {/* Engagement lives below the editing surface: it is reference, not something to act on.
          Only once a public link exists — before publish there is nothing a client could have
          opened, and the panel's "not published yet" line would just restate the banner above. */}
      {quote.public_slug ? <QuoteEngagement quoteId={quote.id} /> : null}

      {showAcceptOnBehalf ? (
        <AcceptOnBehalfModal
          quote={quote}
          onClose={() => setShowAcceptOnBehalf(false)}
          onAccepted={() => {
            setShowAcceptOnBehalf(false);
            // Re-fetch rather than merge: acceptance stamps several fields
            // (status, totals, selected optionals) and appends the audit note.
            void reload();
          }}
        />
      ) : null}
    </section>
  );
}

/** A label + text input that saves on blur when the value changed. */
function FieldRow({
  label,
  value,
  type = "text",
  saving,
  onSave,
}: {
  label: string;
  value?: string;
  type?: string;
  saving: boolean;
  onSave: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value ?? "");
  useEffect(() => setDraft(value ?? ""), [value]);
  return (
    <TextField
      label={label}
      type={type}
      value={draft}
      disabled={saving}
      hint={saving ? "Saving…" : undefined}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== (value ?? "")) onSave(draft);
      }}
    />
  );
}
